import { FC, useEffect, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Navigate, useNavigate } from 'react-router-dom';

import { Button } from 'src/components/button';
import { validateEmail } from 'src/utils/validators/validate-email';
import { Currency } from 'src/shared/enum/Currency';
import { routes } from 'src/constants/routes';
import {
    useSubmitAgreementsAndPay,
    useSubmitAgreementsAndVerify,
} from 'src/lib/hooks/use-submit-agreements';
import { Building } from 'src/shared/types/building';
import { ApiBasket } from 'src/api/basketService';
import { Room } from 'src/shared/types/room';
import { UserChoices } from 'src/shared/types/user-choices';
import { PaymentPlanDto } from 'src/api/paymentPlanService/dto/PaymentPlanDto';
import { checkIsEngBuilding } from 'src/utils/is-english-building';

import { handlePayment } from '../../utils/handle-payment';
import { BookingCalculatedDetails } from '../../api/bookingService';

import { EmailValidationState } from './enum/EmailValidationState';
import { AgreementsView } from './agreements.view';
import { AgreementType } from './types';
import { Agreement } from './agreement';
import { AgreementContent } from './agreements-content';
import { BookingTermsContent } from './booking-terms-content';
import { styles } from './styles';

interface AgreementsContainerProps {
    bookingDetails: BookingCalculatedDetails;
    building: Building;
    basket: ApiBasket;
    roomData: Room;
    userChoices: UserChoices;
    createdPaymentPlan: PaymentPlanDto;
}

export const AgreementsContainer: FC<AgreementsContainerProps> = ({
    bookingDetails,
    building,
    basket,
    roomData,
    userChoices,
    createdPaymentPlan,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [termsCheckboxVal, setTermsCheckboxVal] = useState(false);
    const [licenceCheckboxVal, setLicenceCheckboxVal] = useState(false);
    const [termsEmailAddress, setTermsEmailAddress] = useState('');
    const [licenceEmailAddress, setLicenceEmailAddress] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentAgreement, setCurrentAgreement] = useState<AgreementType>(AgreementType.Terms);
    const [termsEmailValidationState, setTermsEmailValidationState] = useState(
        EmailValidationState.VALID
    );
    const [licenceEmailValidationState, setLicenceEmailValidationState] = useState(
        EmailValidationState.VALID
    );
    const hasRoommates = Boolean(basket.room_mates?.length);
    const hasDiscount = Boolean(basket.products[0].promotion_id);
    const selectedUser = basket.user;
    const paymentRequired = createdPaymentPlan.paymentOptions.some(
        (option) =>
            moment(option.dueDate, 'DD/MM/YYYY', true).isSame(moment(), 'day') &&
            !option.price.isZero()
    );
    const currency = basket.products[0].currency as Currency;
    const isSTL = bookingDetails.bookingDetails.terms.some((term) =>
        term.instalments.some((instalment) =>
            instalment.name.toLocaleLowerCase().includes('bespoke')
        )
    );

    const isEnglishBuilding = checkIsEngBuilding(building);

    const {
        mutate: submitAgreementsAndPay,
        isPending: isSigningAgreementsAndPay,
        error: errorSubmittingAgreementsAndPay,
    } = useSubmitAgreementsAndPay();
    const {
        mutate: submitAgreementsAndVerify,
        isPending: isSigningAgreementsAndVerify,
        error: errorSubmittingAgreementsAndVerify,
    } = useSubmitAgreementsAndVerify();

    const handleGoBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        window.dataLayer.push({
            event: 'go_to_agreements',
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (!basket) {
        navigate(routes.failure.index);
        return null;
    }

    if (
        !userChoices.term ||
        (!isSTL && !userChoices.term.duration) ||
        !userChoices.term.startDate ||
        !userChoices.term.endDate
    ) {
        navigate(routes.failure.index);
        return null;
    }

    const handleEmail = (
        value: string,
        userEmail: string | undefined,
        setValue: (v: string) => void,
        setState: (s: EmailValidationState) => void
    ) => {
        const email = value.trim().toLowerCase();
        setValue(email);

        if (!email.length) {
            setState(EmailValidationState.VALID);
            return;
        }

        if (!validateEmail(email)) {
            setState(EmailValidationState.INVALID_EMAIL);
            return;
        }

        if (!userEmail || email !== userEmail) {
            setState(EmailValidationState.MISMATCHED_EMAIL);
            return;
        }

        setState(EmailValidationState.VALID);
    };

    const isEmailValidTerms = termsEmailValidationState === EmailValidationState.VALID;
    const isEmailValidLicence = licenceEmailValidationState === EmailValidationState.VALID;

    const isBookingTermsSigned = termsCheckboxVal && !!termsEmailAddress && isEmailValidTerms;
    const isLicenceAgreementSigned =
        licenceCheckboxVal && !!licenceEmailAddress && isEmailValidLicence;

    const isButtonDisabled = !isBookingTermsSigned || !isLicenceAgreementSigned;

    const handleSubmit = () => {
        if (!isBookingTermsSigned || !isLicenceAgreementSigned) return;

        if (paymentRequired) {
            submitAgreementsAndPay(
                {
                    basketId: basket.id,
                    agreementsData: {
                        booking_terms_signed: termsCheckboxVal,
                        licence_agreement_signed: licenceCheckboxVal,
                    },
                    updatedAt: basket.updated_date,
                },
                {
                    onSuccess: ({ payment }) => {
                        handlePayment(payment);
                    },
                }
            );

            return;
        }

        submitAgreementsAndVerify(
            {
                basketId: basket.id,
                agreementsData: {
                    booking_terms_signed: termsCheckboxVal,
                    licence_agreement_signed: licenceCheckboxVal,
                },
                updatedAt: basket.updated_date,
            },
            {
                onSuccess: ({ bookingRecordId }) => {
                    navigate(`${routes.processing.index}?booking=${bookingRecordId}`);
                },
            }
        );
    };

    const openDrawer = (type: AgreementType) => {
        setCurrentAgreement(type);
        setDrawerOpen(true);
    };

    const closeDrawer = () => setDrawerOpen(false);

    const goToNextDrawer = () => {
        setCurrentAgreement(AgreementType.Licence);
    };

    const skipDrawer = () => setDrawerOpen(false);

    const emailHandler =
        (setValue: (v: string) => void, setState: (s: EmailValidationState) => void) =>
        (e: ChangeEvent<HTMLInputElement>) =>
            handleEmail(e.target.value, selectedUser?.email, setValue, setState);

    const handleTermsEmail = emailHandler(setTermsEmailAddress, setTermsEmailValidationState);
    const handleLicenceEmail = emailHandler(setLicenceEmailAddress, setLicenceEmailValidationState);

    const bookingTermsAndConditions = (
        <Agreement
            checkboxVal={termsCheckboxVal}
            emailAddress={termsEmailAddress}
            handleEmailChange={handleTermsEmail}
            handleCheckboxChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTermsCheckboxVal(e.target.checked)
            }
            emailValidationState={termsEmailValidationState}
            title="agreements_booking_terms_and_conditions_title"
            id="terms-email"
            subtitle="agreements_please_read_t&c"
            content={<BookingTermsContent building={building} />}
            checkboxContent="agreements_booking_terms_and_conditions_checkbox_content"
            emailContent="agreements_booking_terms_and_conditions_email_content"
            submitButton={
                <Button
                    variant="contained"
                    onClick={goToNextDrawer}
                    css={styles.cta}
                    disabled={!isBookingTermsSigned}
                    id="terms-submit"
                >
                    {t('agreement_next_agreement')}
                </Button>
            }
            name="terms"
        />
    );

    const licenceAgreement = (
        <Agreement
            checkboxVal={licenceCheckboxVal}
            emailAddress={licenceEmailAddress}
            handleEmailChange={handleLicenceEmail}
            handleCheckboxChange={(e: ChangeEvent<HTMLInputElement>) =>
                setLicenceCheckboxVal(e.target.checked)
            }
            emailValidationState={licenceEmailValidationState}
            title={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_title'
                    : 'agreements_licence_agreement_title'
            }
            id="licence-email-input"
            subtitle={
                isEnglishBuilding
                    ? 'agreements_please_read_common_law_tenancy'
                    : 'agreements_please_read_licence_agreement'
            }
            content={<AgreementContent building={building} />}
            checkboxContent={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_checkbox_content'
                    : 'agreements_licence_agreement_checkbox_content'
            }
            emailContent={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_email_content'
                    : 'agreements_licence_agreement_email_content'
            }
            submitButton={
                <Button
                    variant="contained"
                    onClick={skipDrawer}
                    css={styles.cta}
                    disabled={!isLicenceAgreementSigned}
                    id="licence-sumbit"
                >
                    {t('agreements_submit')}
                </Button>
            }
            name="licence-agreement"
        />
    );

    if (!roomData) {
        return <Navigate to={routes.failure.index} />;
    }

    const loading = isSigningAgreementsAndPay || isSigningAgreementsAndVerify;
    const error = errorSubmittingAgreementsAndPay || errorSubmittingAgreementsAndVerify;

    return (
        <AgreementsView
            onGoBack={handleGoBack}
            hideBackButton={false}
            hasRoommates={hasRoommates}
            roomData={roomData}
            paymentPlan={createdPaymentPlan}
            termDuration={userChoices.term.duration}
            termStartDate={userChoices.term.startDate}
            termEndDate={userChoices.term.endDate}
            numberOfStudents={userChoices.numberOfStudents}
            currency={currency}
            hasDiscount={hasDiscount}
            paymentRequired={paymentRequired}
            loading={loading}
            error={error}
            isButtonDisabled={isButtonDisabled}
            onSubmit={handleSubmit}
            terms={{
                checkboxVal: termsCheckboxVal,
                email: termsEmailAddress,
                validation: termsEmailValidationState,
                onCheckbox: setTermsCheckboxVal,
                onEmail: handleTermsEmail,
                isValid: isEmailValidTerms,
            }}
            licence={{
                checkboxVal: licenceCheckboxVal,
                email: licenceEmailAddress,
                validation: licenceEmailValidationState,
                onCheckbox: setLicenceCheckboxVal,
                onEmail: handleLicenceEmail,
                isValid: isEmailValidLicence,
            }}
            drawer={{
                open: drawerOpen,
                onClose: closeDrawer,
                current: currentAgreement,
                agreement: currentAgreement,
                total: 2,
            }}
            agreements={{
                terms: bookingTermsAndConditions,
                licence: licenceAgreement,
            }}
            showTermsDrawer={() => openDrawer(AgreementType.Terms)}
            showLicenceDrawer={() => openDrawer(AgreementType.Licence)}
            building={building}
        />
    );
};
